/**
 * Identity system: ECDSA P-256 for signing, ECDH P-256 for key exchange.
 * Generates keypairs, derives participant ID, computes fingerprints.
 * Persists keys in IndexedDB via persistence module.
 */

import { saveIdentityKeys, loadIdentityKeys } from '../state/persistence'
import type { Identity, StoredIdentity } from '../types'

let cachedIdentity: Identity | null = null

// ============================================================
// Keypair Generation
// ============================================================

/** Generate ECDSA P-256 signing keypair */
async function generateSigningKeyPair(): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    true, // extractable for storage
    ['sign', 'verify'],
  )
}

/** Generate ECDH P-256 key exchange keypair */
async function generateExchangeKeyPair(): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true, // extractable for storage
    ['deriveKey', 'deriveBits'],
  )
}

// ============================================================
// Participant ID
// ============================================================

/**
 * Derive participant ID from the identity public key.
 * SHA-256 of the raw public key, truncated to first 8 bytes (16 hex chars).
 */
export async function deriveParticipantId(
  identityPublicKey: CryptoKey,
): Promise<string> {
  const rawKey = await crypto.subtle.exportKey('raw', identityPublicKey)
  const hash = await crypto.subtle.digest('SHA-256', rawKey)
  const truncated = new Uint8Array(hash, 0, 8)
  return Array.from(truncated)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Convert participant ID to a numeric SFrame Key ID.
 * First 4 hex bytes (8 chars) as a 32-bit unsigned integer.
 */
export function participantIdToKeyId(participantId: string): number {
  return parseInt(participantId.substring(0, 8), 16) >>> 0
}

// ============================================================
// Fingerprint
// ============================================================

/**
 * Compute a human-readable fingerprint from the identity public key.
 * Format: 8 blocks of 4 hex digits, separated by spaces.
 */
export async function computeFingerprint(
  identityPublicKey: CryptoKey,
): Promise<string> {
  const rawKey = await crypto.subtle.exportKey('raw', identityPublicKey)
  const hash = await crypto.subtle.digest('SHA-256', rawKey)
  const bytes = new Uint8Array(hash, 0, 16)
  const blocks: string[] = []
  for (let i = 0; i < 16; i += 2) {
    blocks.push(
      bytes[i]!.toString(16).padStart(2, '0') +
        bytes[i + 1]!.toString(16).padStart(2, '0'),
    )
  }
  return blocks.join(' ')
}

// ============================================================
// Key Export / Import
// ============================================================

async function exportIdentity(identity: Identity): Promise<StoredIdentity> {
  const [identityPrivateKey, identityPublicKey, kxPrivateKey, kxPublicKey] =
    await Promise.all([
      crypto.subtle.exportKey('jwk', identity.signingKeyPair.privateKey),
      crypto.subtle.exportKey('jwk', identity.signingKeyPair.publicKey),
      crypto.subtle.exportKey('jwk', identity.exchangeKeyPair.privateKey),
      crypto.subtle.exportKey('jwk', identity.exchangeKeyPair.publicKey),
    ])

  return {
    identityPrivateKey,
    identityPublicKey,
    kxPrivateKey,
    kxPublicKey,
    participantId: identity.participantId,
    createdAt: identity.createdAt,
  }
}

async function importIdentity(stored: StoredIdentity): Promise<Identity> {
  const [signingPrivate, signingPublic, exchangePrivate, exchangePublic] =
    await Promise.all([
      crypto.subtle.importKey(
        'jwk',
        stored.identityPrivateKey,
        { name: 'ECDSA', namedCurve: 'P-256' },
        true,
        ['sign'],
      ),
      crypto.subtle.importKey(
        'jwk',
        stored.identityPublicKey,
        { name: 'ECDSA', namedCurve: 'P-256' },
        true,
        ['verify'],
      ),
      crypto.subtle.importKey(
        'jwk',
        stored.kxPrivateKey,
        { name: 'ECDH', namedCurve: 'P-256' },
        true,
        ['deriveKey', 'deriveBits'],
      ),
      crypto.subtle.importKey(
        'jwk',
        stored.kxPublicKey,
        { name: 'ECDH', namedCurve: 'P-256' },
        true,
        [],
      ),
    ])

  return {
    signingKeyPair: { privateKey: signingPrivate, publicKey: signingPublic },
    exchangeKeyPair: {
      privateKey: exchangePrivate,
      publicKey: exchangePublic,
    },
    participantId: stored.participantId,
    createdAt: stored.createdAt,
  }
}

// ============================================================
// Core API
// ============================================================

/**
 * Get or create the local identity.
 * Loads from IndexedDB on first call; generates and persists if none exists.
 */
export async function getOrCreateIdentity(): Promise<Identity> {
  if (cachedIdentity) return cachedIdentity

  // Try loading from IndexedDB
  const stored = await loadIdentityKeys()
  if (stored) {
    cachedIdentity = await importIdentity(stored)
    return cachedIdentity
  }

  // Generate new identity
  const [signingKeyPair, exchangeKeyPair] = await Promise.all([
    generateSigningKeyPair(),
    generateExchangeKeyPair(),
  ])

  const participantId = await deriveParticipantId(signingKeyPair.publicKey)

  const identity: Identity = {
    signingKeyPair,
    exchangeKeyPair,
    participantId,
    createdAt: Date.now(),
  }

  // Persist to IndexedDB
  const exported = await exportIdentity(identity)
  await saveIdentityKeys(exported)

  cachedIdentity = identity
  return identity
}

/**
 * Sign data with the local identity ECDSA private key.
 * Returns the raw signature bytes.
 */
export async function sign(data: BufferSource): Promise<ArrayBuffer> {
  const identity = await getOrCreateIdentity()
  return crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    identity.signingKeyPair.privateKey,
    data,
  )
}

/**
 * Verify a signature using a remote peer's ECDSA public key.
 */
export async function verify(
  publicKey: CryptoKey,
  signature: ArrayBuffer,
  data: BufferSource,
): Promise<boolean> {
  return crypto.subtle.verify(
    { name: 'ECDSA', hash: 'SHA-256' },
    publicKey,
    signature,
    data,
  )
}

/**
 * Export the identity public key as a base64url string for signaling messages.
 */
export async function exportPublicKeyBase64(
  publicKey: CryptoKey,
): Promise<string> {
  const raw = await crypto.subtle.exportKey('raw', publicKey)
  return arrayBufferToBase64Url(raw)
}

/**
 * Import a peer's identity public key from base64url.
 */
export async function importPublicKeyFromBase64(
  base64url: string,
): Promise<CryptoKey> {
  const raw = base64UrlToArrayBuffer(base64url)
  return crypto.subtle.importKey(
    'raw',
    raw,
    { name: 'ECDSA', namedCurve: 'P-256' },
    true,
    ['verify'],
  )
}

/**
 * Import a peer's ECDH public key from base64url.
 */
export async function importKxPublicKeyFromBase64(
  base64url: string,
): Promise<CryptoKey> {
  const raw = base64UrlToArrayBuffer(base64url)
  return crypto.subtle.importKey(
    'raw',
    raw,
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    [],
  )
}

/** Clear the cached identity (for testing or identity reset) */
export function clearCachedIdentity(): void {
  cachedIdentity = null
}

// ============================================================
// Base64url helpers
// ============================================================

export function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!)
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function base64UrlToArrayBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = new ArrayBuffer(binary.length)
  const view = new Uint8Array(bytes)
  for (let i = 0; i < binary.length; i++) {
    view[i] = binary.charCodeAt(i)
  }
  return bytes
}
